import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { handleGetRequest, handlePatchRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import InfoGrid from '../../components/common/InfoGrid'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { formatTime } from '../../utils/date.util'
import SuspendCustomerModal from '../../components/admin/SuspendCustomerModal'
import { showToaster } from '../../utils/show-toaster.util'
import { useMutation } from '@tanstack/react-query'
import DisplayActiveSuspended from '../../components/admin/DisplayActiveSuspended'
import ConfirmModal from '../../components/common/ConfirmModal'

export default function CustomerDetailsPage() {
  const { id } = useParams()

  const {
    data: customer,
    isLoading,
    error,
  } = useQuery({
    queryKey: QueryKeys.adminCustomerDetails(id),
    queryFn: () => handleGetRequest(`/admin/customer/${id}`),
    select: (res) => res?.data,
  })

  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false)
  const [showUnsuspendConfirm, setShowUnsuspendConfirm] = useState(false)

  const unSuspendMutation = useMutation({
    mutationFn: () => handlePatchRequest(`/admin/customer/${customer.id}/unsuspend`),
    onSuccess: () => {
      showToaster('success', 'Unsuspended the customer successfully')
      QueryKeys.adminCustomerDetails.invalidate(id)
      QueryKeys.adminListCustomers.invalidateAll()
    },
    onError: () => {
      showToaster('error', 'Error suspending the customer')
    },
  })
  const handleUnsuspend = async () => {
    await unSuspendMutation.mutateAsync()
  }

  const handleSuspend = () => {
    setIsSuspendModalOpen(true)
  }

  const customerInfoItems = [
    { label: 'Name', value: customer?.name, capitalize: true },
    { label: 'Phone', value: customer?.phoneNumber },
    {
      label: 'Status',
      value: (
        <>
          <DisplayActiveSuspended
            isActive={customer?.isActive}
            suspendedBy={customer?.suspendedBy}
          />
          {customer?.suspendedAt && (
            <span className="text-foreground-secondary text-sm block mt-1">
              {`Suspended at: ${formatTime(customer?.suspendedAt)}`} by {customer?.suspender?.name}
            </span>
          )}
          {customer?.suspensionReason && (
            <span className="text-foreground-secondary text-sm block mt-1">
              {`Reason: ${customer?.suspensionReason}`}
            </span>
          )}
        </>
      ),
    },
    { label: 'Create date', value: formatTime(customer?.createdAt) },
  ]

  const statusItems = [
    {
      label: 'Active tickets',
      value: customer?.activeTickets || 0,
      className: 'text-success-500',
    },
    {
      label: 'Violation reports',
      value: customer?.violationReports || 0,
      className: 'text-success-500',
    },
  ]

  const customerActions = (
    <>
      <div className="flex gap-4">
        <button
          className="bg-neutral-100 border border-button-border rounded-[4px] px-6 py-2 text-foreground font-normal"
          onClick={customer?.isActive ? handleSuspend : handleUnsuspend}
        >
          {customer?.isActive ? 'Suspend' : 'Unsuspend'}
        </button>
      </div>

      <div className="flex flex-col gap-6 mt-12">
        {statusItems.map((item, index) => (
          <div key={index} className="flex justify-between gap-2">
            <span className="font-inter font-medium text-md text-foreground">{item.label}:</span>
            <span className={`font-inter font-medium text-md ${item.className}`}>{item.value}</span>
          </div>
        ))}
      </div>
    </>
  )

  if (isLoading) {
    return (
      <div>
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return <div className="text-red-500">Error loading customer details</div>
  }

  return (
    <>
      <InfoGrid infoItems={customerInfoItems} actions={customerActions} />
      <SuspendCustomerModal
        isOpen={isSuspendModalOpen}
        onClose={() => setIsSuspendModalOpen(false)}
        customer={customer}
        customerId={id}
      />
      <ConfirmModal
        isOpen={showUnsuspendConfirm}
        onClose={() => setShowUnsuspendConfirm(false)}
        onConfirm={handleUnsuspend}
        title="Confirm Unsuspend"
        message={`Are you sure you want to unsuspend ${customer?.name || 'this customer'}?`}
        confirmText="Yes, Unsuspend"
        cancelText="Cancel"
        isConfirming={unSuspendMutation.isLoading}
      />
    </>
  )
}
